import "react-complex-tree/lib/style-modern.css";
import "./MyDocuments.override.scss";

import {
  ActionIcon,
  Box,
  Button,
  FileButton,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import {
  IconArrowsMove,
  IconDownload,
  IconFile,
  IconFileImport,
  IconFolder,
  IconFolderOpen,
  IconHistory,
  IconPencil,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ControlledTreeEnvironment,
  Tree,
  type TreeItem,
  type TreeItemRenderContext,
  UncontrolledTreeEnvironment,
} from "react-complex-tree";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import DateTimeDisp from "src/components/shared/DateTime/DateTimeDisp";
import { PageContainer } from "src/styled";

import styles from "./MyDocuments.module.scss";
import { type IMyDocumentsItem, useMyDocuments } from "./useMyDocuments";
import type { ComboboxItem } from "@mantine/core";
import {
  type IMyDocumentsTreeData,
  getDocumentLabel,
  MOVE_TREE_ID,
  type MyDocumentsActionItem,
  normalizeDestinationParentId,
  ROOT_PARENT,
  TREE_ID,
  useMyDocumentsTree,
} from "./useMyDocumentsTree";

const getFileBaseName = (item: IMyDocumentsItem) => {
  const extension = item.ext ? `.${item.ext}` : "";

  if (extension && item.name?.endsWith(extension)) {
    return item.name.slice(0, -extension.length);
  }

  return item.name ?? "";
};

const buildFileName = (item: IMyDocumentsItem, baseName: string) => {
  const trimmedBaseName = baseName.trim();
  if (!trimmedBaseName) return item.name;

  return item.ext ? `${trimmedBaseName}.${item.ext}` : trimmedBaseName;
};

const getTreeParentId = (parent: string | undefined, rootId?: string) => {
  if (!rootId) return ROOT_PARENT;
  if (!parent || parent === ROOT_PARENT || parent === rootId) return rootId;
  return parent;
};

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** index;

  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

const formatDate = (date: string) =>
  date && dayjs(date).isValid() ? dayjs(date).format("DD MMM YYYY HH:mm") : "-";

const formatSize = (node: IMyDocumentsTreeData) => {
  if (node.type === 2) return formatBytes(node.len);

  const itemCount = node.childCount ?? 0;
  return `${itemCount} ${itemCount === 1 ? "item" : "items"}`;
};

const SPREADSHEET_ACCEPT =
  ".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv";

const MyDocuments = () => {
  const {
    data,
    isLoading,
    addFile,
    importFile,
    addFolder,
    getFile,
    getHistory,
    renameFile,
    removeFile,
    moveFile,
    renameFolder,
    removeFolder,
    moveFolder,
  } = useMyDocuments();
  const { colorScheme } = useMantineColorScheme();
  const [renameTarget, setRenameTarget] =
    useState<MyDocumentsActionItem | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [moveTarget, setMoveTarget] = useState<MyDocumentsActionItem | null>(
    null,
  );
  const [moveValue, setMoveValue] = useState(ROOT_PARENT);
  const {
    items,
    rootId,
    moveDataProvider,
    moveExpandedItems,
    selectedItems,
    viewState: mainTreeViewState,
    setSelectedItems,
    setFocusedItem,
    handleDrop,
    handleSelectItems,
    handleFocusItem,
    handleExpandItem,
    handleCollapseItem,
    pendingMoves,
    executePendingMoves,
    clearPendingMoves,
  } = useMyDocumentsTree({
    data,
    moveTarget,
    moveFile,
    moveFolder,
  });
  const nameForId = (id?: string) => {
    if (!id) return "";
    if (rootId && id === rootId) return ROOT_PARENT;
    const found = data?.list?.find(d => d.id === id);
    return found?.name ?? id;
  };
  const getSelectedParent = useCallback(() => {
    if (selectedItems && selectedItems.length === 1) {
      const sel = selectedItems[0];
      if (rootId && sel === rootId) return ROOT_PARENT;
      const selItem = items?.[sel];
      if (selItem && selItem.isFolder) return String(sel);
    }

    return ROOT_PARENT;
  }, [rootId, selectedItems, items]);
  const [dragMoveConfirmOpened, setDragMoveConfirmOpened] = useState(false);
  const [createFolderOpened, setCreateFolderOpened] = useState(false);
  const [createFolderName, setCreateFolderName] = useState("");
  const [deleteTargets, setDeleteTargets] = useState<
    MyDocumentsActionItem[] | null
  >(null);
  const [contextTarget, setContextTarget] =
    useState<MyDocumentsActionItem | null>(null);
  const [contextPos, setContextPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [historyOpened, setHistoryOpened] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState<any | null>(null);

  const historyRows = useMemo(() => {
    const list = historyData?.list;
    if (!list) return [];

    return Array.isArray(list) ? list : Object.values(list);
  }, [historyData]);

  useEffect(() => {
    if (!historyOpened) return;

    let mounted = true;

    void (async () => {
      try {
        setHistoryLoading(true);
        const result = await getHistory();
        if (!mounted) return;
        setHistoryData(result ?? null);
      } catch (e) {
        if (mounted) setHistoryData(null);
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [historyOpened, getHistory]);

  useEffect(() => {
    if (pendingMoves && pendingMoves.length > 0) {
      setDragMoveConfirmOpened(true);
    }
  }, [pendingMoves]);

  const handleUpload = useCallback(
    async (file: File | null) => {
      if (!file) return;

      const parent = getSelectedParent();
      await addFile(parent, file);
    },
    [addFile, getSelectedParent],
  );

  const handleImport = useCallback(
    async (file: File | null) => {
      if (!file) return;

      const parent = getSelectedParent();
      await importFile(parent, file);
    },
    [getSelectedParent, importFile],
  );

  const handleAddFolder = useCallback(() => {
    setCreateFolderName("");
    setCreateFolderOpened(true);
  }, []);

  const closeCreateFolder = useCallback(() => {
    setCreateFolderOpened(false);
    setCreateFolderName("");
  }, []);

  const confirmCreateFolder = useCallback(async () => {
    const name = createFolderName.trim();
    if (!name) return;
    const parent = getSelectedParent();
    await addFolder(parent, name);
    closeCreateFolder();
  }, [addFolder, closeCreateFolder, createFolderName, getSelectedParent]);

  const openMove = useCallback((item: MyDocumentsActionItem) => {
    setMoveTarget(item);
    setMoveValue(ROOT_PARENT);
  }, []);

  const closeMove = useCallback(() => {
    setMoveTarget(null);
    setMoveValue(ROOT_PARENT);
  }, []);

  const [moveConfirmOpened, setMoveConfirmOpened] = useState(false);

  const confirmMove = useCallback(async () => {
    if (!moveTarget || !moveValue) return;

    const currentParent = getTreeParentId(moveTarget.parent, rootId);
    const destinationParentId = normalizeDestinationParentId(moveValue, rootId);

    if (currentParent === destinationParentId) {
      setMoveConfirmOpened(false);
      closeMove();
      return;
    }

    if (moveTarget.isFolder) {
      await moveFolder(moveValue, moveTarget.id);
    } else {
      await moveFile(moveValue, moveTarget.id);
    }

    setMoveConfirmOpened(false);
    closeMove();
  }, [closeMove, moveFile, moveFolder, moveTarget, moveValue, rootId]);

  const openRename = useCallback((item: MyDocumentsActionItem) => {
    setRenameTarget(item);
    setRenameValue(item.isFolder ? (item.name ?? "") : getFileBaseName(item));
  }, []);

  const closeRename = useCallback(() => {
    setRenameTarget(null);
    setRenameValue("");
  }, []);

  const handleRenameInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.currentTarget.value;
      if (!renameTarget || renameTarget.isFolder) {
        setRenameValue(raw);
        return;
      }

      const extWithDot = renameTarget.ext ? `.${renameTarget.ext}` : "";

      if (extWithDot && raw.endsWith(extWithDot)) {
        setRenameValue(raw.slice(0, -extWithDot.length));
      } else {
        // Keep whatever the user typed as the basename; the extension will be shown
        // in the input's rightSection and re-appended on save.
        setRenameValue(raw);
      }
    },
    [renameTarget],
  );

  const handleRename = useCallback(async () => {
    if (!renameTarget || !renameValue.trim()) return;

    const nextName = renameTarget.isFolder
      ? renameValue.trim()
      : buildFileName(renameTarget, renameValue);
    if (renameTarget.isFolder) {
      await renameFolder(renameTarget.id, nextName);
    } else {
      await renameFile(renameTarget.id, nextName);
    }
    closeRename();
  }, [closeRename, renameFolder, renameTarget, renameValue, renameFile]);

  const handleRemove = useCallback(
    async (item: MyDocumentsActionItem) => {
      if (item.isFolder) {
        await removeFolder(item.id);
      } else {
        await removeFile(item.id);
      }
    },
    [removeFile, removeFolder],
  );

  const openDeleteConfirm = useCallback(
    (item: MyDocumentsActionItem) => {
      const selectedDeleteIds =
        selectedItems.length > 1 && selectedItems.includes(item.id)
          ? selectedItems
          : [item.id];

      const nextDeleteTargets = selectedDeleteIds
        .map(id => {
          if (rootId && id === rootId) return null;

          const document = data?.list?.find(entry => entry.id === id);
          if (!document) return null;

          return {
            ...document,
            isFolder: document.type !== 2,
          };
        })
        .filter((entry): entry is MyDocumentsActionItem => Boolean(entry));

      setDeleteTargets(
        nextDeleteTargets.length > 0 ? nextDeleteTargets : [item],
      );
    },
    [data?.list, rootId, selectedItems],
  );

  const closeDeleteConfirm = useCallback(() => {
    setDeleteTargets(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargets || deleteTargets.length === 0) return;

    for (const target of deleteTargets) {
      await handleRemove(target);
    }
    setSelectedItems([]);
    setFocusedItem(undefined);
    closeDeleteConfirm();
  }, [
    closeDeleteConfirm,
    deleteTargets,
    handleRemove,
    setFocusedItem,
    setSelectedItems,
  ]);

  const renderMoveDestinationTitle = useCallback(
    ({
      item,
      context,
    }: {
      item: TreeItem<IMyDocumentsTreeData>;
      context: TreeItemRenderContext;
    }) => {
      const destinationValue =
        rootId && item.index === rootId ? ROOT_PARENT : String(item.index);
      const isSelected = moveValue === destinationValue;

      return (
        <Flex
          align="center"
          gap={8}
          px={6}
          py={4}
          style={{
            borderRadius: 6,
            cursor: "pointer",
            backgroundColor: isSelected
              ? "var(--mantine-color-blue-light)"
              : "transparent",
          }}
          onClick={() => setMoveValue(destinationValue)}>
          <Box mt={2}>
            {context.isExpanded ? (
              <IconFolderOpen size={14} />
            ) : (
              <IconFolder size={14} />
            )}
          </Box>
          <Text size="sm" lineClamp={1}>
            {getDocumentLabel(item.data, rootId)}
          </Text>
        </Flex>
      );
    },
    [moveValue, rootId],
  );

  const renderItemTitle = ({
    item,
    context,
  }: {
    item: TreeItem<IMyDocumentsTreeData>;
    context: TreeItemRenderContext;
  }) => {
    const file = item.data;
    const isFolder = item.isFolder ?? false;
    const actionItem = { ...file, isFolder };
    const icon = isFolder ? (
      context.isExpanded ? (
        <IconFolderOpen size={16} />
      ) : (
        <IconFolder size={16} />
      )
    ) : (
      <IconFile size={16} />
    );

    return (
      <Flex
        gap="md"
        align="center"
        w="100%"
        style={{ minWidth: 0, height: "100%" }}
        onDoubleClick={() => {
          if (!isFolder && file?.id) {
            void getFile(file.id);
          }
        }}
        onContextMenu={event => {
          event.preventDefault();
          event.stopPropagation();
          setContextTarget(actionItem);
          setContextPos({ x: event.clientX, y: event.clientY });
        }}>
        <Flex gap={8} align="center" style={{ minWidth: 0, flex: 1 }}>
          <Box mt={2}>{icon}</Box>
          <Text lineClamp={1} style={{ minWidth: 0 }} size="sm">
            {getDocumentLabel(file, rootId)}
          </Text>
        </Flex>

        <Flex
          gap="md"
          align="center"
          justify="flex-end"
          style={{ minWidth: 430, flexShrink: 0 }}>
          <Stack gap={0} align="flex-end" miw={120} style={{ flexShrink: 0 }}>
            <Text size="xs" c="dimmed" ta="right">
              {formatSize(file)}
            </Text>
          </Stack>

          <Stack gap={0} align="flex-end" miw={140} style={{ flexShrink: 0 }}>
            <Text size="xs" c="dimmed" ta="right">
              {formatDate(file.createddate)}
            </Text>
          </Stack>

          <Stack gap={0} align="flex-end" miw={140} style={{ flexShrink: 0 }}>
            <Text size="xs" c="dimmed" ta="right">
              {formatDate(file.modifydate)}
            </Text>
          </Stack>
        </Flex>
      </Flex>
    );
  };

  return (
    <PageContainer>
      <Breadcrumbs />

      <Stack gap="lg" pt="md" h="calc(100% - 52px - 16px - 6px)">
        <Paper
          withBorder
          radius="lg"
          p="xl"
          h="100%"
          style={{ display: "flex", flexDirection: "column" }}>
          <Flex
            justify="space-between"
            align="flex-start"
            gap="md"
            wrap="wrap"
            pb={16}>
            <Stack gap={6} style={{ flex: 1 }}>
              <Group gap="xs" align="center">
                <Text fz={28} lh={1.1} pb={4}>
                  My Documents
                </Text>
                <ActionIcon
                  size="lg"
                  aria-label="Open document history"
                  onClick={() => setHistoryOpened(true)}>
                  <IconHistory size={18} />
                </ActionIcon>
              </Group>
            </Stack>

            <Flex gap="md" wrap="wrap" align="center">
              <Button
                variant="light"
                leftSection={<IconFolder size={16} />}
                loading={isLoading}
                onClick={() => {
                  void handleAddFolder();
                }}>
                New Folder
              </Button>

              <FileButton accept={SPREADSHEET_ACCEPT} onChange={handleImport}>
                {props => (
                  <Button
                    variant="light"
                    leftSection={<IconFileImport size={16} />}
                    loading={isLoading}
                    {...props}>
                    Import
                  </Button>
                )}
              </FileButton>

              <FileButton onChange={handleUpload}>
                {props => (
                  <Button
                    variant="light"
                    leftSection={<IconUpload size={16} />}
                    loading={isLoading}
                    {...props}>
                    Upload File
                  </Button>
                )}
              </FileButton>
            </Flex>
          </Flex>

          {isLoading && !data ? (
            <Flex justify="center" py="xl">
              <Loader />
            </Flex>
          ) : rootId && data?.list && data.list.length > 0 ? (
            <div
              className={colorScheme === "dark" ? "rct-dark" : undefined}
              style={{
                flex: 1,
                overflowY: "auto",
                height: "100%",
              }}>
              <Flex
                gap="md"
                align="center"
                justify="space-between"
                px={12}
                py={8}
                className={
                  styles.header +
                  " " +
                  (colorScheme === "dark" ? styles.dark : styles.light)
                }>
                <Stack
                  gap={0}
                  align="start"
                  miw={120}
                  style={{ flexShrink: 0 }}>
                  <Text size="xs" c="dimmed" lh={1.1}>
                    Name
                  </Text>
                </Stack>
                <Flex gap="1rem">
                  <Stack
                    gap={0}
                    align="flex-end"
                    miw={120}
                    style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed" lh={1.1}>
                      Size
                    </Text>
                  </Stack>
                  <Stack
                    gap={0}
                    align="flex-end"
                    miw={140}
                    style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed" lh={1.1}>
                      Created
                    </Text>
                  </Stack>
                  <Stack
                    gap={0}
                    align="flex-end"
                    miw={140}
                    style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed" lh={1.1}>
                      Modified
                    </Text>
                  </Stack>
                </Flex>
              </Flex>
              <ControlledTreeEnvironment
                items={items}
                getItemTitle={item => getDocumentLabel(item.data, rootId)}
                // primary action disabled — open on double-click only
                viewState={mainTreeViewState}
                canSearch={false}
                canRename={true}
                canDragAndDrop={true}
                canDropOnFolder={true}
                canDropOnNonFolder={true}
                canReorderItems={false}
                canDrag={dragItems =>
                  !rootId || !dragItems.some(i => i.index === rootId)
                }
                onSelectItems={handleSelectItems}
                onFocusItem={handleFocusItem}
                onDrop={handleDrop}
                onExpandItem={handleExpandItem}
                onCollapseItem={handleCollapseItem}
                renderItemTitle={renderItemTitle}>
                <Tree
                  treeId={TREE_ID}
                  rootItem={rootId}
                  treeLabel="My Documents"
                />
              </ControlledTreeEnvironment>
            </div>
          ) : (
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap={16}
              py={40}>
              <Text c="dimmed">
                <IconFolder size={44} />
              </Text>
              <Text size="lg" c="dimmed">
                No documents found.
              </Text>
            </Flex>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={Boolean(renameTarget)}
        onClose={closeRename}
        title={`Rename ${renameTarget?.isFolder ? "Folder" : "File"}`}
        size="md"
        centered>
        <Stack>
          <TextInput
            label={renameTarget?.isFolder ? "Name" : "File name"}
            value={renameValue}
            onChange={handleRenameInputChange}
            rightSection={
              renameTarget && !renameTarget.isFolder && renameTarget.ext ? (
                <Text size="sm" c="dimmed" style={{ opacity: 0.8 }}>
                  .{renameTarget.ext}
                </Text>
              ) : undefined
            }
            rightSectionWidth={
              renameTarget?.ext
                ? `calc(${renameTarget?.ext.length} * 0.875rem + 4px)`
                : undefined
            }
            rightSectionProps={{
              style: {
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: 12,
              },
            }}
            autoFocus
          />
          <Group justify="right">
            <Button variant="default" onClick={closeRename}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleRename();
              }}
              loading={isLoading}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={createFolderOpened}
        onClose={closeCreateFolder}
        title="New Folder"
        size="md"
        centered>
        <Stack>
          <TextInput
            label="Folder name"
            value={createFolderName}
            onChange={event => setCreateFolderName(event.currentTarget.value)}
            autoFocus
          />
          <Group justify="right">
            <Button variant="default" onClick={closeCreateFolder}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                void confirmCreateFolder();
              }}
              loading={isLoading}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={historyOpened}
        onClose={() => setHistoryOpened(false)}
        title="History"
        size="xl">
        <Stack
          gap="md"
          justify="center"
          align="center"
          style={{ minHeight: 200 }}>
          {historyLoading ? (
            <Loader />
          ) : historyRows.length > 0 ? (
            <Stack gap="xs">
              {historyRows.map((row: any, index: number) => {
                let author = row.full_name ?? row.userid ?? "-";
                if (row.rolename && row.full_name) {
                  author = `${row.rolename} - ${row.full_name}`;
                }
                const key =
                  row.hid ??
                  `${row.timestamp ?? row.timestampUTC ?? index}-${row.rid ?? row.userid ?? row.action ?? index}`;

                return (
                  <Paper key={key} withBorder radius="md" p="sm">
                    <Flex align="flex-start" justify="space-between" gap="md">
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} lineClamp={2}>
                          {row.action ?? "-"}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {author}
                        </Text>
                      </Box>
                      <DateTimeDisp
                        lt={row.timestamp}
                        utc={row.timestampUTC}
                        dir="column"
                        containerProps={{
                          style: { flexShrink: 0, alignItems: "flex-end" },
                        }}
                        textProps={{ size: "xs", c: "dimmed" }}
                      />
                    </Flex>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              Document history will appear here.
            </Text>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(deleteTargets && deleteTargets.length > 0)}
        onClose={closeDeleteConfirm}
        title={
          deleteTargets && deleteTargets.length > 1
            ? `Delete ${deleteTargets.length} Items?`
            : `Delete ${deleteTargets?.[0]?.isFolder ? "Folder" : "File"}?`
        }
        size="md"
        centered>
        <Stack>
          <Text size="sm">
            {deleteTargets && deleteTargets.length > 1
              ? `This will remove ${deleteTargets.length} selected items.`
              : deleteTargets?.[0]
                ? `This will remove ${deleteTargets[0].isFolder ? "the folder" : "the file"} "${deleteTargets[0].name}".`
                : "This item will be removed."}
          </Text>
          <Group justify="right">
            <Button variant="default" onClick={closeDeleteConfirm}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                void confirmDelete();
              }}
              loading={isLoading}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(moveTarget)}
        onClose={closeMove}
        title={`Move ${moveTarget?.isFolder ? "Folder" : "File"}`}
        size="xl"
        centered>
        <Stack>
          <Text size="sm">Destination folder</Text>
          <Paper withBorder radius="md" p="md" style={{ maxHeight: 420 }}>
            <div
              className={colorScheme === "dark" ? "rct-dark" : undefined}
              style={{ overflowY: "auto", maxHeight: 380 }}>
              <UncontrolledTreeEnvironment
                dataProvider={moveDataProvider}
                getItemTitle={item => getDocumentLabel(item.data, rootId)}
                viewState={{
                  [MOVE_TREE_ID]: {
                    expandedItems: moveExpandedItems,
                  },
                }}
                renderItemTitle={renderMoveDestinationTitle}>
                <Tree
                  treeId={MOVE_TREE_ID}
                  rootItem={rootId ?? ROOT_PARENT}
                  treeLabel="Move destination"
                />
              </UncontrolledTreeEnvironment>
            </div>
          </Paper>
          <Group justify="right">
            <Button variant="default" onClick={closeMove}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setMoveConfirmOpened(true);
              }}
              loading={isLoading}>
              Move
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={moveConfirmOpened}
        onClose={() => setMoveConfirmOpened(false)}
        title="Confirm Move"
        size="md"
        centered>
        <Stack>
          <Text size="sm">
            {`Are you sure you want to move "${moveTarget?.name ?? ""}" to "${moveValue === ROOT_PARENT ? ROOT_PARENT : (data?.list?.find(d => d.id === moveValue)?.name ?? moveValue)}"?`}
          </Text>
          <Group justify="right">
            <Button
              variant="default"
              onClick={() => setMoveConfirmOpened(false)}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={() => {
                void confirmMove();
              }}
              loading={isLoading}>
              Move
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={dragMoveConfirmOpened}
        onClose={() => {
          clearPendingMoves();
          setDragMoveConfirmOpened(false);
        }}
        title="Confirm Move"
        size="md"
        centered>
        <Stack>
          {pendingMoves && pendingMoves.length > 0 ? (
            (() => {
              const destId = pendingMoves[0].destinationParentId;
              const destLabel =
                rootId && destId === rootId ? ROOT_PARENT : nameForId(destId);

              if (pendingMoves.length === 1) {
                const src = nameForId(pendingMoves[0].id);
                return (
                  <Text size="sm">{`Are you sure you want to move "${src}" to /${destLabel}?`}</Text>
                );
              }

              return (
                <Text size="sm">{`Are you sure you want to move ${pendingMoves.length} items to /${destLabel}?`}</Text>
              );
            })()
          ) : (
            <Text size="sm">No moves pending.</Text>
          )}
          <Group justify="right">
            <Button
              variant="default"
              onClick={() => {
                clearPendingMoves();
                setDragMoveConfirmOpened(false);
              }}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={() => {
                void executePendingMoves();
                setDragMoveConfirmOpened(false);
              }}
              loading={isLoading}>
              Move
            </Button>
          </Group>
        </Stack>
      </Modal>

      {contextTarget && contextPos ? (
        <Menu
          opened={Boolean(contextTarget)}
          onClose={() => {
            setContextTarget(null);
            setContextPos(null);
          }}
          withinPortal
          shadow="md"
          width={200}>
          <Menu.Target>
            <div
              style={{
                position: "fixed",
                top: contextPos.y,
                left: contextPos.x,
                width: 1,
                height: 1,
                pointerEvents: "none",
              }}
            />
          </Menu.Target>

          <Menu.Dropdown>
            {!contextTarget.isFolder ? (
              <Menu.Item
                leftSection={<IconDownload size={14} />}
                onClick={() => {
                  void getFile(contextTarget.id);
                  setContextTarget(null);
                  setContextPos(null);
                }}>
                Download
              </Menu.Item>
            ) : null}
            <Menu.Item
              leftSection={<IconPencil size={14} />}
              onClick={() => {
                if (contextTarget) openRename(contextTarget);
                setContextTarget(null);
                setContextPos(null);
              }}>
              Rename
            </Menu.Item>
            <Menu.Item
              leftSection={<IconArrowsMove size={14} />}
              onClick={() => {
                if (contextTarget) openMove(contextTarget);
                setContextTarget(null);
                setContextPos(null);
              }}>
              Move
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => {
                if (contextTarget) openDeleteConfirm(contextTarget);
                setContextTarget(null);
                setContextPos(null);
              }}>
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : null}
    </PageContainer>
  );
};

export default MyDocuments;
